import moment from 'moment';
import { getLastMessagePreview, JUICYHOUSE_CALL_EVENT, MESSAGE_TYPE } from './matrixRoomUtils';
import { BROWSER_TIMEZONE, calculateDaysRemaining } from '../function';
import {
  MESSAGE_REPLY_DEADLINE_DAYS,
  MESSAGE_REPLY_DEADLINE_DAYS_EXTENDED,
  MESSAGE_REPLY_DEADLINE_DAYS_NOTIFY,
} from '../../constants/message';

const isServerEventId = (eventId) =>
  typeof eventId === 'string' && eventId.length > 0 && eventId.startsWith('$');

const findLatestReadableServerEvent = (room) => {
  if (!room) return null;

  const preferred = getLastMessagePreview(room)?.event || room.getLastLiveEvent?.();
  const preferredId = preferred?.getId?.();
  if (preferred && isServerEventId(preferredId)) return preferred;

  const timeline =
    room.getLiveTimeline?.().getEvents?.() ||
    room.timeline ||
    [];

  if (!Array.isArray(timeline) || timeline.length === 0) return null;

  for (let i = timeline.length - 1; i >= 0; i -= 1) {
    const ev = timeline[i];
    const id = ev?.getId?.();
    if (ev && isServerEventId(id)) return ev;
  }

  return null;
};

/**
 * Mark a Matrix room as read by sending read markers / receipts.
 *
 * Matrix does NOT automatically clear unread counts when you render a timeline.
 * Clients must explicitly send read receipts / read markers.
 *
 * Prefers MatrixClient#setRoomReadMarkers() (modern API) and falls back to
 * MatrixClient#sendReadReceipt() if needed.
 *
 * @param {Object} client - Matrix client instance
 * @param {Object} room - Matrix room instance
 * @param {Object} [options]
 * @param {Object} [options.event] - Specific MatrixEvent to mark as read
 * @returns {Promise<{success: boolean, eventId?: string, error?: string}>}
 */
export const markRoomAsRead = async (client, room, options = {}) => {
  if (!client || !room) return { success: false, error: 'Missing client or room' };

  try {
    const preferredEvent = findLatestReadableServerEvent(room) || options?.event;

    const eventId = preferredEvent?.getId?.();
    if (!isServerEventId(eventId)) {
      return { success: false, error: 'No server eventId to mark as read' };
    }

    // Preferred modern API: sets both m.fully_read and m.read (depending on homeserver)
    if (typeof client.setRoomReadMarkers === 'function') {
      // matrix-js-sdk v39+: (roomId, rmEventId, rrEvent?, rpEvent?)
      try {
        await client.setRoomReadMarkers(room.roomId, eventId, preferredEvent);
      } catch (e) {
        // Some SDK/homeserver combos are picky about rrEvent; at minimum set fully_read.
        await client.setRoomReadMarkers(room.roomId, eventId);
      }
    }

    // Ensure a read receipt is sent (this is what most servers use to clear "unread").
    if (typeof client.sendReadReceipt === 'function') {
      await client.sendReadReceipt(preferredEvent);
      return { success: true, eventId };
    }

    // If we got here, setRoomReadMarkers may have succeeded, but there's no receipt API.
    if (typeof client.setRoomReadMarkers === 'function') {
      return { success: true, eventId };
    }

    return { success: false, error: 'Client does not support read markers/receipt API' };
  } catch (e) {
    return { success: false, error: e?.message || 'Failed to mark room as read' };
  }
};

/**
 * Send a lightweight call status notice to a Matrix room.
 *
 * @param {Object} client - Matrix client instance
 * @param {Object} params
 * @param {boolean} params.isInitialized - Matrix init state
 * @param {string} params.roomId - Matrix room id
 * @param {string} params.bodyText - Notice text to send
 * @returns {Promise<boolean>} true when sent
 */
export const sendCallNoticeMessage = async (
  client,
  { isInitialized, roomId, bodyText }
) => {
  const message = typeof bodyText === 'string' ? bodyText.trim() : '';
  if (!message || !client || !isInitialized || !roomId) return false;

  try {
    await client.sendEvent?.(roomId, MESSAGE_TYPE, {
      msgtype: 'm.notice',
      body: message,
    });
    return true;
  } catch {
    return false;
  }
};

/**
 * Send a custom Juicyhouse call event (not m.room.message — avoids chat timeline).
 * @param {Object} client - Matrix client
 * @param {Object} params
 * @param {boolean} params.isInitialized
 * @param {string} params.roomId
 * @param {Record<string, unknown>} params.content - Event content payload
 * @returns {Promise<boolean>}
 */
export const sendJuicyhouseCallEvent = async (client, { isInitialized, roomId, content }) => {
  if (!client || !isInitialized || !roomId || !content || typeof content !== 'object') return false;
  try {
    await client.sendEvent?.(roomId, JUICYHOUSE_CALL_EVENT, content);
    return true;
  } catch {
    return false;
  }
};

const MS_PER_DAY = 24 * 60 * 60 * 1000;

/**
 * Room priority: (first-message tip + creator replied, within 60 days of first tip) OR API isPriority.
 *
 * @param {{ isFirstMessageTip?: boolean, isCreatorReplyToTip?: boolean, firstTipCreatedAt?: string|null, isPriority?: boolean }|null|undefined} otherUser
 * @returns {boolean}
 */
export const computeRoomIsPriority = (otherUser) => {
  const isFirstMessageTip = Boolean(otherUser?.isFirstMessageTip);
  const isCreatorReplyToTip = Boolean(otherUser?.isCreatorReplyToTip);
  const firstTipCreatedAt = otherUser?.firstTipCreatedAt || null;
  const getPriority = Boolean(otherUser?.isPriority);

  const daysSinceFirstTip = firstTipCreatedAt
    ? Math.floor((Date.now() - new Date(firstTipCreatedAt).getTime()) / MS_PER_DAY)
    : null;
  const isFirstTipWithin60Days = daysSinceFirstTip !== null && daysSinceFirstTip <= 60;
  const isFirstMessagePriority = isFirstMessageTip && isCreatorReplyToTip && isFirstTipWithin60Days;

  return (isFirstMessagePriority || getPriority) ? true : false;
};

export const getFirstTipDeadlineInfo = (firstTipCreatedAt, isExtended = false) => {
  let notifyDays = MESSAGE_REPLY_DEADLINE_DAYS_NOTIFY;
  let maxDays = MESSAGE_REPLY_DEADLINE_DAYS;
  if (isExtended) {
    maxDays = maxDays + MESSAGE_REPLY_DEADLINE_DAYS_EXTENDED;
    notifyDays = notifyDays + MESSAGE_REPLY_DEADLINE_DAYS_EXTENDED;
  }

  if (!firstTipCreatedAt) {
    return null;
  }

  const createdAt = moment(firstTipCreatedAt).tz(BROWSER_TIMEZONE);
  const createdAtMs = createdAt.valueOf();
  if (!Number.isFinite(createdAtMs)) {
    return null;
  }

  const MS_PER_DAY = 24 * 60 * 60 * 1000; // 1 day in milliseconds
  const tenDayDeadline = createdAtMs + maxDays * MS_PER_DAY; // 10 days in milliseconds
  const daysLeft = calculateDaysRemaining(tenDayDeadline);
  // const daysLeft = 2;

  if (daysLeft < 0) { // if the deadline is in the past, return null
    return null;
  }

  let daysLabel = null;

  if (daysLeft === 0) {
    daysLabel = 'Ends today';
  } else if (daysLeft === 1) {
    daysLabel = '1 day remaining';
  } else if (daysLeft > 1 && daysLeft <= notifyDays) {
    daysLabel = `${daysLeft} days remaining`;
  }

  return {
    label: daysLabel,
    daysLeft: daysLeft,
  };
};

/**
 * Update a JSON key in the `content.body` of a Matrix event by sending an m.replace edit.
 *
 * Assumes the original event content has a `body` field containing a JSON string.
 *
 * @param {Object} client - Matrix client instance
 * @param {Object} params
 * @param {string} params.roomId - Matrix room ID
 * @param {string} params.eventId - Original event ID to edit
 * @param {string} params.key - JSON key to update inside parsed body
 * @param {*} params.value - Value to assign to the key
 * @returns {Promise<boolean>} true on success, false on any failure
 */
export const updateMatrixEventJsonBodyKey = async (
  client,
  { roomId, eventId, key, value }
) => {
  if (!client || typeof client.getRoom !== 'function') {
    // eslint-disable-next-line no-console
    return false;
  }
  if (!roomId || typeof roomId !== 'string') {
    // eslint-disable-next-line no-console
    return false;
  }
  if (!eventId || typeof eventId !== 'string') {
    // eslint-disable-next-line no-console
    return false;
  }
  if (!key || typeof key !== 'string') {
    // eslint-disable-next-line no-console
    return false;
  }

  try {
    let event = null;

    // 1) Try to resolve from the in-memory room timeline (fast path)
    const room = client.getRoom(roomId);
    if (room && typeof room.findEventById === 'function') {
      event = room.findEventById(eventId);
    }

    // 2) Fallback: fetch directly from the homeserver REST API (if supported)
    if (!event && typeof client.fetchRoomEvent === 'function') {
      event = await client.fetchRoomEvent(roomId, eventId);
    }

    if (!event) {
      // eslint-disable-next-line no-console
      return false;
    }

    // Normalise content depending on whether this is a MatrixEvent instance or raw JSON
    const baseContent =
      typeof event.getContent === 'function'
        ? event.getContent()
        : event.content || {};

    const content = { ...baseContent };
    const body = content.body;

    if (typeof body !== 'string' || !body.trim()) {
      // eslint-disable-next-line no-console
      return false;
    }

    let parsed;
    try {
      parsed = JSON.parse(body);
    } catch (parseErr) {
      // eslint-disable-next-line no-console
      return false;
    }
    
    const updated = {
      ...parsed,
      [key]: value,
    };
    const newBody = JSON.stringify(updated);

    if (typeof client.sendEvent !== 'function') {
      // eslint-disable-next-line no-console
      return false;
    }

    const editContent = {
      msgtype: 'm.text',
      body: `* ${newBody}`, // Matrix-spec fallback text
      'm.new_content': { msgtype: 'm.text', body: newBody },
      'm.relates_to': { rel_type: 'm.replace', event_id: eventId },
    };

    try {
      await client.sendEvent(roomId, MESSAGE_TYPE, editContent);
      return true;
    } catch (sendErr) {
      // eslint-disable-next-line no-console
      return false;
    }
  } catch (error) {
    // eslint-disable-next-line no-console
    return false;
  }
};


